import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Lock, Heart, Loader2, Plus, Shield } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const AnonymousPage = () => {
  const { user, refreshUser } = useAuth();
  const [worries, setWorries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newWorry, setNewWorry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (user?.is_premium) {
      fetchWorries();
    } else {
      setLoading(false);
    }
  }, [user?.is_premium, page]);

  const fetchWorries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/worries?page=${page}&limit=10`);
      setWorries(response.data.worries);
      setTotalPages(response.data.pages);
    } catch (error) {
      if (error.response?.status === 403) {
        // Refresh user to check premium status
        await refreshUser();
      }
      toast.error('Failed to load anonymous posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newWorry.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/worries`, {
        content: newWorry
      });
      setWorries([response.data, ...worries]);
      setNewWorry('');
      toast.success('Your worry has been shared anonymously');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSupport = async (worryId) => {
    try {
      const response = await axios.post(`${API}/api/worries/${worryId}/support`);
      setWorries(worries.map(w => 
        w.id === worryId 
          ? { ...w, support_count: response.data.support_count }
          : w
      ));
    } catch (error) {
      toast.error('Failed to support');
    }
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  // Non-premium view
  if (!user?.is_premium) {
    return (
      <div className="min-h-screen bg-[#F7F5F2]">
        <Header />
        
        <div className="content-container pt-20">
          <div className="max-w-2xl mx-auto py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-[#E27D60]/10 flex items-center justify-center mx-auto mb-6">
              <Lock className="h-10 w-10 text-[#E27D60]" />
            </div>
            
            <h1 className="text-3xl font-bold text-stone-800 mb-4 font-['Outfit']">
              Anonymous Sharing
            </h1>
            
            <p className="text-lg text-stone-600 mb-8 max-w-md mx-auto">
              A safe space to share your worries and concerns without revealing your identity. 
              Only premium members can access this feature.
            </p>

            <Card className="anonymous-card rounded-2xl max-w-md mx-auto mb-8">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-[#E27D60]" />
                  <span className="font-semibold text-stone-800">Premium Benefits</span>
                </div>
                <ul className="text-left text-stone-600 space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <span className="text-[#6B8E7B]">✓</span>
                    Share your worries anonymously
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#6B8E7B]">✓</span>
                    Read and support others anonymously
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#6B8E7B]">✓</span>
                    Your identity is never revealed
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#6B8E7B]">✓</span>
                    Safe space for nursing professionals
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Link to="/subscribe">
              <Button className="btn-premium text-lg px-8 py-6" data-testid="subscribe-premium-button">
                <Lock className="h-5 w-5 mr-2" />
                Unlock for €5/month
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Premium view
  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      <Header />
      
      <div className="content-container pt-20">
        <div className="max-w-2xl mx-auto py-8">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="h-6 w-6 text-[#E27D60]" />
            <h1 className="text-2xl font-bold text-stone-800 font-['Outfit']">Anonymous Sharing</h1>
            <span className="premium-badge">Premium</span>
          </div>

          {/* Create Worry */}
          <Card className="anonymous-card rounded-2xl mb-6">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit}>
                <Textarea
                  placeholder="Share your worry anonymously... Your identity will never be revealed."
                  value={newWorry}
                  onChange={(e) => setNewWorry(e.target.value)}
                  className="rounded-xl resize-none min-h-[120px] mb-4 bg-white"
                  data-testid="worry-input"
                />
                <Button
                  type="submit"
                  className="btn-premium"
                  disabled={submitting || !newWorry.trim()}
                  data-testid="submit-worry-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Posting...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Share Anonymously
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Worries List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#E27D60]" />
            </div>
          ) : worries.length === 0 ? (
            <Card className="border-stone-200 rounded-2xl bg-white">
              <CardContent className="p-12 text-center">
                <Shield className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-stone-800 mb-2">No anonymous posts yet</h3>
                <p className="text-stone-500">Be the first to share in this safe space.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {worries.map((worry, index) => (
                <Card 
                  key={worry.id} 
                  className="anonymous-card rounded-2xl"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`worry-card-${worry.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-[#E27D60]/20 flex items-center justify-center flex-shrink-0">
                        <Shield className="h-5 w-5 text-[#E27D60]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-stone-800">Anonymous Nurse</span>
                          <span className="text-xs text-stone-400">{formatDate(worry.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-stone-600 leading-relaxed whitespace-pre-wrap mb-4">
                      {worry.content}
                    </p>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSupport(worry.id)}
                      className="rounded-full text-[#E27D60] hover:bg-[#E27D60]/10"
                      data-testid={`support-button-${worry.id}`}
                    >
                      <Heart className="h-4 w-4 mr-2" />
                      {worry.support_count || 0} support
                    </Button>
                  </CardContent>
                </Card>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-full"
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-4 text-stone-600">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="rounded-full"
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnonymousPage;
